import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Loader2, Plus, Trash2, Edit2, Zap, Search, X, 
  ToggleLeft, Settings2, Layers, FlaskConical, Globe,
  CheckCircle2, XCircle, Package, Rocket, RotateCcw,
  ChevronRight
} from "lucide-react";
import axios from "axios";

const CATEGORIES = [
  { value: "seo", label: "SEO", icon: Globe, color: "bg-green-100 text-green-700" },
  { value: "ui", label: "UI/UX", icon: Layers, color: "bg-blue-100 text-blue-700" },
  { value: "functionality", label: "Functionality", icon: Settings2, color: "bg-purple-100 text-purple-700" },
  { value: "integration", label: "Integration", icon: Zap, color: "bg-amber-100 text-amber-700" },
  { value: "experiment", label: "Experiment", icon: FlaskConical, color: "bg-pink-100 text-pink-700" },
];

const AdminFeatures = () => {
  const { token } = useAuth();
  const [features, setFeatures] = useState([]);
  const [patches, setPatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showPatchModal, setShowPatchModal] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  
  const [form, setForm] = useState({
    key: "",
    name: "",
    description: "",
    category: "functionality",
    is_enabled: false,
    percentage_rollout: 100,
    config: "{}"
  });

  const [patchForm, setPatchForm] = useState({
    name: "",
    description: "",
    changes: []
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchFeatures = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/features`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeatures(response.data);
    } catch (error) {
      toast.error("Failed to fetch features");
    }
  }, [BACKEND_URL, token]);

  const fetchPatches = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/patches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPatches(response.data);
    } catch (error) {
      console.error("Failed to fetch patches:", error);
    }
  }, [BACKEND_URL, token]);

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchFeatures(), fetchPatches()]);
      setLoading(false);
    };
    loadData();
  }, [fetchFeatures, fetchPatches]);

  const resetForm = () => {
    setForm({
      key: "",
      name: "",
      description: "",
      category: "functionality",
      is_enabled: false,
      percentage_rollout: 100,
      config: "{}"
    });
    setEditingFeature(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.key || !form.name) {
      toast.error("Key and name are required");
      return;
    }

    // Validate config JSON
    try {
      JSON.parse(form.config);
    } catch {
      toast.error("Config must be valid JSON");
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...form,
        config: JSON.parse(form.config)
      };

      if (editingFeature) {
        await axios.put(
          `${BACKEND_URL}/api/admin/features/${editingFeature.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Feature updated - changes apply immediately!");
      } else {
        await axios.post(
          `${BACKEND_URL}/api/admin/features`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Feature created - no rebuild required!");
      }
      
      setShowModal(false);
      resetForm();
      fetchFeatures();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save feature");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (feature) => {
    setEditingFeature(feature);
    setForm({
      key: feature.key,
      name: feature.name,
      description: feature.description || "",
      category: feature.category,
      is_enabled: feature.is_enabled,
      percentage_rollout: feature.percentage_rollout,
      config: JSON.stringify(feature.config || {}, null, 2)
    });
    setShowModal(true);
  };

  const handleToggle = async (feature) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/features/${feature.id}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Feature "${feature.name}" ${feature.is_enabled ? 'disabled' : 'enabled'} - live instantly!`);
      fetchFeatures();
    } catch (error) {
      toast.error("Failed to toggle feature");
    }
  };

  const handleDelete = async (feature) => {
    if (!window.confirm(`Delete feature "${feature.name}"?`)) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/features/${feature.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Feature deleted");
      fetchFeatures();
    } catch (error) {
      toast.error("Failed to delete feature");
    }
  };

  const handleDeployPatch = async (patch) => {
    if (!window.confirm(`Deploy patch "${patch.name}"? This will apply all changes immediately.`)) return;
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/patches/${patch.id}/deploy`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchPatches();
      fetchFeatures();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deploy patch");
    }
  };

  const handleRollbackPatch = async (patch) => {
    if (!window.confirm(`Rollback patch "${patch.name}"? This will reverse all changes.`)) return;
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/patches/${patch.id}/rollback`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchPatches();
      fetchFeatures();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to rollback patch");
    }
  };

  const filteredFeatures = features.filter(f => {
    const matchesSearch = f.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          f.key.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || f.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryInfo = (category) => {
    return CATEGORIES.find(c => c.value === category) || CATEGORIES[2];
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-features">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-foreground mb-2">
              Feature Flags
            </h1>
            <p className="text-muted-foreground">Patch-based deployment - changes apply instantly without rebuild</p>
          </div>
          <Button 
            onClick={() => { resetForm(); setShowModal(true); }}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700"
            data-testid="add-feature-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Feature Flag
          </Button>
        </div>

        {/* Info Banner */}
        <div className="bg-gradient-to-r from-emerald-50 to-cyan-50 border border-emerald-200 rounded-xl p-4">
          <div className="flex gap-3">
            <Zap className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-emerald-800">
              <p className="font-medium mb-1">Universal Patch-Based Deployment</p>
              <p className="text-emerald-700">Toggle features on/off <strong>instantly</strong> without CI/CD pipelines or code changes.</p>
              <ul className="list-disc list-inside mt-2 text-emerald-700">
                <li>No rebuild or redeploy required</li>
                <li>Changes apply immediately for all users</li>
                <li>Rollback features instantly if issues arise</li>
                <li>Group changes into patches for coordinated releases</li>
              </ul>
            </div>
          </div>
        </div>

        <Tabs defaultValue="features" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="features" className="flex items-center gap-2">
              <ToggleLeft className="w-4 h-4" />
              Feature Flags ({features.length})
            </TabsTrigger>
            <TabsTrigger value="patches" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Patch History ({patches.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="features" className="space-y-4 mt-4">
            {/* Search & Filter */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search features..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Features List */}
            <div className="bg-white rounded-xl shadow-soft overflow-hidden">
              {filteredFeatures.length === 0 ? (
                <div className="p-12 text-center">
                  <ToggleLeft className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No feature flags found</h3>
                  <p className="text-muted-foreground mb-4">Create feature flags to control functionality without deploying</p>
                  <Button onClick={() => { resetForm(); setShowModal(true); }} variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Feature Flag
                  </Button>
                </div>
              ) : (
                <div className="divide-y">
                  {filteredFeatures.map((feature) => {
                    const catInfo = getCategoryInfo(feature.category);
                    const CatIcon = catInfo.icon;
                    return (
                      <div 
                        key={feature.id} 
                        className="p-4 hover:bg-gray-50 transition-colors"
                        data-testid={`feature-${feature.id}`}
                      >
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-2">
                              <Switch
                                checked={feature.is_enabled}
                                onCheckedChange={() => handleToggle(feature)}
                                data-testid={`toggle-${feature.id}`}
                              />
                              <h3 className="font-medium text-foreground">{feature.name}</h3>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${catInfo.color}`}>
                                <CatIcon className="w-3 h-3" />
                                {catInfo.label}
                              </span>
                              {feature.percentage_rollout < 100 && (
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                  {feature.percentage_rollout}% rollout
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <code className="bg-gray-100 px-2 py-0.5 rounded font-mono text-xs text-gray-600">
                                {feature.key}
                              </code>
                              {feature.description && (
                                <span className="text-muted-foreground truncate">{feature.description}</span>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(feature)}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(feature)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="patches" className="space-y-4 mt-4">
            {/* Patches List */}
            <div className="bg-white rounded-xl shadow-soft overflow-hidden">
              {patches.length === 0 ? (
                <div className="p-12 text-center">
                  <Package className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No patch deployments</h3>
                  <p className="text-muted-foreground">Patches are created when you group multiple feature changes together</p>
                </div>
              ) : (
                <div className="divide-y">
                  {patches.map((patch) => (
                    <div key={patch.id} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-medium text-foreground">{patch.name}</h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              patch.status === 'deployed' ? 'bg-green-100 text-green-700' :
                              patch.status === 'rolled_back' ? 'bg-red-100 text-red-700' :
                              'bg-amber-100 text-amber-700'
                            }`}>
                              {patch.status}
                            </span>
                          </div>
                          {patch.description && (
                            <p className="text-sm text-muted-foreground mb-2">{patch.description}</p>
                          )}
                          <div className="text-xs text-muted-foreground">
                            {patch.changes.length} changes • Created {new Date(patch.created_at).toLocaleDateString()}
                            {patch.deployed_at && ` • Deployed ${new Date(patch.deployed_at).toLocaleDateString()}`}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {patch.status === 'pending' && (
                            <Button
                              size="sm"
                              onClick={() => handleDeployPatch(patch)}
                              className="bg-emerald-600 hover:bg-emerald-700"
                            >
                              <Rocket className="w-4 h-4 mr-1" />
                              Deploy
                            </Button>
                          )}
                          {patch.status === 'deployed' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRollbackPatch(patch)}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                            >
                              <RotateCcw className="w-4 h-4 mr-1" />
                              Rollback
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Feature Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold">
                    {editingFeature ? "Edit Feature Flag" : "Create Feature Flag"}
                  </h2>
                  <Button variant="ghost" size="sm" onClick={() => { setShowModal(false); resetForm(); }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block">
                        Key <span className="text-red-500">*</span>
                      </label>
                      <Input
                        value={form.key}
                        onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                        placeholder="enable_new_feature"
                        disabled={!!editingFeature}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="New Feature"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1 block">Description</label>
                    <Textarea
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="What does this feature do?"
                      rows={2}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Category</label>
                      <Select value={form.category} onValueChange={(value) => setForm({ ...form, category: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map(cat => (
                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Rollout %</label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={form.percentage_rollout}
                        onChange={(e) => setForm({ ...form, percentage_rollout: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1 block">Configuration (JSON)</label>
                    <Textarea
                      value={form.config}
                      onChange={(e) => setForm({ ...form, config: e.target.value })}
                      placeholder='{"key": "value"}'
                      rows={3}
                      className="font-mono text-sm"
                    />
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Switch
                      checked={form.is_enabled}
                      onCheckedChange={(checked) => setForm({ ...form, is_enabled: checked })}
                    />
                    <span className="text-sm font-medium">
                      {form.is_enabled ? (
                        <span className="text-green-600 flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4" /> Feature Enabled
                        </span>
                      ) : (
                        <span className="text-gray-500 flex items-center gap-1">
                          <XCircle className="w-4 h-4" /> Feature Disabled
                        </span>
                      )}
                    </span>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setShowModal(false); resetForm(); }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                      {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                      {editingFeature ? "Update Feature" : "Create Feature"}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminFeatures;
